import axios from 'axios';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const BASE_HOST = isLocalhost
  ? `http://${window.location.hostname}:3009`
  : window.location.origin;

const API_BASE_URL = isLocalhost
  ? `${BASE_HOST}/api`
  : '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.response.use(null, async (error) => {
  const { config } = error;
  if (!config || !config.retry) {
    return Promise.reject(error);
  }
  config.__retryCount = config.__retryCount || 0;
  if (config.__retryCount >= config.retry) {
    return Promise.reject(error);
  }
  config.__retryCount += 1;
  await new Promise((resolve) => setTimeout(resolve, config.retryDelay || 1000));
  return api(config);
});

export const formatImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;
  const cleanUrl = imageUrl.startsWith('/api/') ? imageUrl.substring(4) : imageUrl;
  const normalizedUrl = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
  return isLocalhost ? `${BASE_HOST}${normalizedUrl}` : `/api${normalizedUrl}`;
};

export const fetchProducts = async () => {
  try {
    const response = await api.get('/productos');
    const rawProducts = Array.isArray(response.data) ? response.data : [];
    const grouped = {};
    rawProducts.forEach(product => {
      if (!product.id) return;
      const isUniqueId = product.codigo_barras || `no_barcode_${product.id}`;
      if (!grouped[isUniqueId]) {
        grouped[isUniqueId] = {
          id: product.id,
          name: product.nombre,
          image: formatImageUrl(product.imagen),
          price: parseFloat(product.precio_venta) || 0,
          stock: 0,
          categoryId: product.categoria_id,
          categoryName: product.categoria?.nombre || "Sin categoría",
          description: product.descripcion || "",
          codigo_barras: product.codigo_barras,
          sucursalesDisponibles: [],
          colores: new Set(),
          promotions: product.promociones?.map(promo => ({
            id: promo.id,
            type: promo.tipo,
            value: parseFloat(promo.valor)
          })) || []
        };
      }
      if (product.stock > 0) {
        grouped[isUniqueId].stock += product.stock;
        grouped[isUniqueId].sucursalesDisponibles.push({
          sucursal_id: product.id_sucursal,
          sucursal_nombre: product.sucursal?.nombre || `Sucursal ${product.id_sucursal}`,
          stock: product.stock,
          producto_id: product.id
        });
      }
      let productColors = product.colores;
      if (typeof productColors === 'string') {
        try { productColors = JSON.parse(productColors.replace(/'/g, '"')); } catch (e) { productColors = []; }
      }
      if (Array.isArray(productColors)) {
        productColors.forEach(c => grouped[isUniqueId].colores.add(c));
      }
    });
    return Object.values(grouped).map(g => ({ ...g, colores: Array.from(g.colores) }));
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const fetchCategories = async () => {
  try {
    const response = await api.get('/categorias');
    return response.data.map(category => ({
      id: category.id,
      name: category.nombre,
      description: category.descripcion
    }));
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const fetchProductById = async (productId) => {
  try {
    const response = await api.get(`/productos/${productId}`);
    const product = response.data;
    return {
      id: product.id,
      name: product.nombre,
      image: formatImageUrl(product.imagen),
      price: parseFloat(product.precio_venta),
      stock: product.stockGrupal !== undefined ? product.stockGrupal : product.stock,
      categoryId: product.categoria_id,
      categoryName: product.categoria?.nombre || "Sin categoría",
      description: product.descripcion || "",
      sucursalesDisponibles: product.sucursalesDisponibles || [],
      coloresAgrupados: product.coloresAgrupados || [],
      promotions: product.promociones?.map(promo => ({
        id: promo.id,
        type: promo.tipo,
        value: parseFloat(promo.valor)
      })) || []
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const fetchActivePromotions = async () => {
  try {
    const response = await api.get('/promociones');
    return response.data.map(promotion => ({
      ...promotion,
      fecha_inicio: new Date(promotion.fecha_inicio),
      fecha_fin: new Date(promotion.fecha_fin),
      productos: promotion.productos?.map(product => ({
        ...product,
        imagen: formatImageUrl(product.imagen)
      })) || []
    }));
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const sendOrder = async (orderData) => {
  try {
    return { success: true, message: "Pedido enviado" };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const checkApiConnection = async () => {
  try {
    await api.get('/ping', { timeout: 5000 });
    return true;
  } catch (error) {
    return false;
  }
};

export const getActiveAnnouncements = async () => {
  try {
    const response = await api.get('/anuncios/activos');
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchCatalogoInfo = async () => {
  try {
    const response = await api.get('/catalogo-info/publica');
    return response.data;
  } catch (error) {
    return {
      telefono: '+591 74842996',
      email: 'info@channels.com',
      direccion: 'Calle Omiste entre Sucre, frente a Impuestos Nacionales',
      horarios_semana: 'Lun-Sáb: 8:00-20:00',
      horarios_domingo: 'Dom: 9:00-14:00',
      facebook_url: 'https://www.facebook.com/share/1BJs4XPzVu/',
      instagram_url: 'https://www.instagram.com',
      tiktok_url: 'https://www.tiktok.com/@accesorioschannels',
      whatsapp_url: 'https://wa.me/59174842996',
      grupos_whatsapp: [
        { name: "Grupo 1", link: "https://chat.whatsapp.com/LqwALOUhgol9k9uFSgkijy", members: "250+", status: "activo" },
        { name: "Grupo 2", link: "https://chat.whatsapp.com/D8Z4wfqn730A2MXEqxLIT3", members: "230+", status: "activo" }
      ],
      descripcion_empresa: 'Bisutería Channels',
      logo_url: null,
      banner_url: null
    };
  }
};

export { api, BASE_HOST };
export default api;